import { ApisauceInstance } from 'apisauce';

import getDriverRewardsOutput from '../../api/getDriverRewardsOutput';
import { EmbedProgram } from './getEmbedProgramsByConfigId';
import { PeriodBonusDocument } from './fetchProgramPeriodDocuments';

/**
 * One program in the PROGRAMS_RESPONSE. A program appears when the driver has a
 * reward record either in its ongoing period (the backend `active` period, or
 * the date-containing one when none is active) or, failing that, in their most
 * recent earlier period. A record exists for every participant, qualified or
 * not, so its presence is the enrolment signal; programs the driver has never
 * had a record in are omitted entirely.
 *
 * `ongoingBonusDocument` is a bare reward output (same shape as
 * PERIODS_RESPONSE docs) carrying `id` + `periodStatus`. When it's a most-recent
 * fallback rather than the current period, its `periodStatus` is non-`active`,
 * which is how the embed tells the two apart (rendering the latter as history).
 */
export interface ProgramWithOngoing {
  bonusConfigDocId: string;
  name: string;
  ongoingBonusDocument: PeriodBonusDocument;
}

/**
 * Answers a PROGRAMS_REQUEST: one entry per program the driver has a record in,
 * preferring the ongoing period and falling back to their most recent one.
 *
 * `today` is passed in (YYYY-MM-DD) rather than read from the clock so the
 * date fallback is testable; the handler stamps the current date.
 */
const fetchProgramsWithOngoing = async ({
  api,
  driverId,
  programsByConfigId,
  today,
}: {
  api: ApisauceInstance;
  driverId: string;
  programsByConfigId: { [bonusConfigDocId: string]: EmbedProgram };
  today: string;
}): Promise<ProgramWithOngoing[]> => {
  const entries = await Promise.all(
    Object.entries(programsByConfigId).map(
      async ([bonusConfigDocId, { name, periods }]) => {
        const fetchDoc = (
          period: BonusTypes.BonusPeriod,
        ): Promise<PeriodBonusDocument | undefined> =>
          getDriverRewardsOutput({
            api,
            driver: driverId,
            bonusProgramId: bonusConfigDocId,
            intervalId: period.id,
          })
            .then((response) =>
              response.ok && response.data?.bonusDatasetRecordId
                ? {
                    ...response.data,
                    id: response.data.bonusDatasetRecordId,
                    periodStatus: period.status,
                  }
                : undefined,
            )
            .catch(() => undefined);

        // The ongoing period: the backend's active period (no client-side date
        // math), falling back to date containment only when none is active.
        const ongoingPeriod =
          periods.find((period) => period.status === 'active') ??
          periods.find(
            (period) =>
              period.startDate.slice(0, 10) <= today &&
              today <= period.endDate.slice(0, 10),
          );

        if (ongoingPeriod) {
          const doc = await fetchDoc(ongoingPeriod);
          if (doc) {
            return { bonusConfigDocId, name, ongoingBonusDocument: doc };
          }
        }

        // No record in the ongoing period → fall back to the driver's most
        // recent period that has one. Fan out the remaining periods newest-first
        // and take the first with a record; omit only if none exists. (Only the
        // fallback path fans out — a featured driver resolves in one call above.)
        const fallbackPeriods = [...periods]
          .sort((a, b) => b.startDate.localeCompare(a.startDate))
          .filter((period) => period.id !== ongoingPeriod?.id);
        const fallbackDocs = await Promise.all(fallbackPeriods.map(fetchDoc));
        const mostRecentDoc = fallbackDocs.find((doc) => !!doc);
        if (mostRecentDoc) {
          return {
            bonusConfigDocId,
            name,
            ongoingBonusDocument: mostRecentDoc,
          };
        }

        return undefined;
      },
    ),
  );

  return entries.filter((entry): entry is ProgramWithOngoing => !!entry);
};

export default fetchProgramsWithOngoing;
